import React, { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { countryOptions, utils } from '../../../core';
import CheckboxInput from '../../ui/CheckboxInput';
import Form from '../../ui/Form';
import Modal from '../../ui/Modal';
import PhoneNumberInput from '../../ui/PhoneNumberInput';
import RadioGroup from '../../ui/RadioGroup';
import SelectInput from '../../ui/SelectInput';
import TextInput from '../../ui/TextInput';
import PractitionerFormContext from './PractitionerFormContext';

export interface LocationInputsProps {
  index: number;
  field?: any;
  values?: any;
}

const CheckboxRow = styled.div`
  margin: 20px 0;
`;

const LocationInputs = ({ index, field, values }: LocationInputsProps) => {
  const { errors, setValue, getValues, watch } = useFormContext();
  const {
    editing,
    disabledFields,
    loadPractices,
    isAddPractitionerContacts,
    onSelectSearchedLocation,
    isEditOnly,
    showSearchLocationButton,
  } = useContext(PractitionerFormContext);

  const [isShowingModal, setShowingModal] = useState(false);

  const addContact = watch(`locations[${index}].addContact`);
  const search = watch(`locations[${index}].search`);

  const checkDisabled = (fieldName: string) => {
    const [first, ...name] = fieldName.split('.');
    if (!name.length) {
      return disabledFields?.some((item) => item === first);
    }
    return disabledFields?.some((item) => item === name.join('.'));
  };

  useEffect(() => {
    if (!editing) {
      setValue(`locations[${index}].addContact`, !!field?.practitionerContact);

      if (!!field?.practitionerContact) {
        setValue(`locations[${index}].practitionerContact`, {
          phoneNumber: field?.practitionerContact.phoneNumber,
          faxNumber: field?.practitionerContact.faxNumber,
          emailAddress: field?.practitionerContact.emailAddress,
          deactivated: field?.practitionerContact.deactivated,
          verified: field?.practitionerContact.verified,
        });
      } else {
        setValue(`locations[${index}].practitionerContact`, null);
      }
    }
  }, [editing, field, index, setValue]);

  useEffect(() => {
    if (values && search) {
      for (const [fieldName, value] of Object.entries(values)) {
        setValue(`locations[${index}].${fieldName}`, value);
      }
    }
  }, [values, search, index, setValue]);

  const resetPractitionerContactFields = () => {
    setValue(`locations[${index}].addContact`, false);

    if (isEditOnly) {
      setValue(`locations[${index}].practitionerContact`, null);
    } else {
      setValue(`locations[${index}].practitionerContact`, {
        phoneNumber: '',
        faxNumber: '',
        emailAddress: '',
        deactivated: 'false',
        verified: 'false',
      });
    }
  };

  const handleCheckboxChange = (e: any) => {
    if (e.target.checked === false) {
      setShowingModal(true);
    } else {
      setValue(`locations[${index}].addContact`, true);
    }
  };

  const handleRemovePractitionerContacts = () => {
    resetPractitionerContactFields();
    setShowingModal(false);
  };

  return (
    <React.Fragment>
      <Form.RowLabel>Location Information</Form.RowLabel>
      <Form.Row>
        {isEditOnly && showSearchLocationButton && (
          <Form.Controller
            name={`locations[${index}].search`}
            defaultValue={''}
            render={(props: any) => {
              return (
                <SelectInput
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  placeholder="Search by Name"
                  label="Search"
                  isAsync
                  isClearable
                  loadOptions={loadPractices}
                  onChange={(option: any) => {
                    if (!option) {
                      for (const [fieldName] of Object.entries(values)) {
                        setValue(`locations[${index}].${fieldName}`, '');
                      }
                    }
                    props.onChange(option);
                    onSelectSearchedLocation && onSelectSearchedLocation(option, index);
                  }}
                />
              );
            }}
          />
        )}

        <Form.Controller
          as={TextInput}
          type="text"
          name={`locations[${index}].name`}
          label="Practice Name"
          placeholder="Practice Name"
          rules={utils.validate('practiceName', 'required')}
          disabled={checkDisabled(`locations[${index}].name`) || search}
          defaultValue={field?.name}
          errorMessage={errors?.locations?.[index]?.name?.message}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`locations[${index}].locationName`}
          label="Location Name"
          placeholder="Location Name"
          rules={utils.validate('locationName', 'required')}
          defaultValue={field?.locationName}
          errorMessage={errors?.locations?.[index]?.locationName?.message}
          disabled={checkDisabled(`locations[${index}].locationName`) || search}
        />
      </Form.Row>

      <Form.RowLabel>Location Contact Information</Form.RowLabel>
      <Form.Row>
        <Form.Controller
          as={TextInput}
          type="text"
          name={`locations[${index}].address`}
          label="Address 1"
          placeholder="Address 1"
          rules={utils.validate('address', 'required')}
          defaultValue={field?.address}
          errorMessage={errors?.locations?.[index]?.address?.message}
          disabled={checkDisabled(`locations[${index}].address`) || search}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`locations[${index}].address2`}
          label="Address 2"
          placeholder="Address 2"
          rules={utils.validate('address2')}
          defaultValue={field?.address2}
          errorMessage={errors?.locations?.[index]?.address2?.message}
          disabled={checkDisabled(`locations[${index}].address2`) || search}
        />

        <Form.Controller
          as={SelectInput}
          options={countryOptions}
          name={`locations[${index}].country`}
          label="Country"
          rules={utils.validate('country', 'required')}
          defaultValue={field?.country}
          errorMessage={errors?.locations?.[index]?.country?.message}
          disabled={checkDisabled(`locations[${index}].country`) || search}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`locations[${index}].state`}
          label="Province/State"
          placeholder="Province/State"
          rules={utils.validate('state', 'required')}
          defaultValue={field?.state}
          errorMessage={errors?.locations?.[index]?.state?.message}
          disabled={checkDisabled(`locations[${index}].state`) || search}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`locations[${index}].city`}
          label="City"
          placeholder="City"
          rules={utils.validate('city', 'required')}
          defaultValue={field?.city}
          errorMessage={errors?.locations?.[index]?.city?.message}
          disabled={checkDisabled(`locations[${index}].city`) || search}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`locations[${index}].postalCode`}
          label="Postal Code"
          placeholder="Postal Code"
          rules={utils.validate('postalCode', 'required')}
          defaultValue={field?.postalCode}
          errorMessage={errors?.locations?.[index]?.postalCode?.message}
          disabled={checkDisabled(`locations[${index}].postalCode`) || search}
        />

        <Form.Controller
          as={PhoneNumberInput}
          type="tel"
          name={`locations[${index}].phoneNumber`}
          label="Office Phone Number"
          placeholder="Office Phone Number"
          rules={utils.validate('phoneNumber')}
          defaultValue={field?.phoneNumber}
          errorMessage={errors?.locations?.[index]?.phoneNumber?.message}
          disabled={checkDisabled(`locations[${index}].phoneNumber`) || search}
        />

        <Form.Controller
          as={PhoneNumberInput}
          type="tel"
          name={`locations[${index}].faxNumber`}
          label="Office Fax Number"
          placeholder="Office Fax Number"
          rules={utils.validate('faxNumber')}
          defaultValue={field?.faxNumber}
          errorMessage={errors?.locations?.[index]?.faxNumber?.message}
          disabled={checkDisabled(`locations[${index}].faxNumber`) || search}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`locations[${index}].emailAddress`}
          label="Office Email"
          placeholder="Office Email"
          rules={utils.validate('emailAddress')}
          defaultValue={field?.emailAddress}
          errorMessage={errors?.locations?.[index]?.emailAddress?.message}
          disabled={checkDisabled(`locations[${index}].emailAddress`) || search}
        />
      </Form.Row>

      <Form.RowLabel>Location Status Information</Form.RowLabel>
      <Form.Row>
        <Form.Controller
          as={''}
          name={`locations[${index}].deactivated`}
          label="Deactivated"
          defaultValue={field?.deactivated || 'false'}
          render={(props: any) => {
            return (
              <RadioGroup
                name={props.name}
                onChange={props.onChange}
                initialValue={field?.deactivated || 'false'}
                title={'Deactivated'}
              >
                <RadioGroup.Input
                  id={`${props.name}1`}
                  label="True"
                  value="true"
                  disabled={checkDisabled(`locations[${index}].deactivated`) || search}
                />
                <RadioGroup.Input
                  id={`${props.name}2`}
                  label="False"
                  value="false"
                  disabled={checkDisabled(`locations[${index}].deactivated`) || search}
                />
              </RadioGroup>
            );
          }}
        />

        <Form.Controller
          as={''}
          name={`locations[${index}].verified`}
          label="Verified"
          defaultValue={field?.verified || 'false'}
          render={(props: any) => {
            return (
              <RadioGroup
                name={props.name}
                onChange={props.onChange}
                initialValue={field?.verified || 'false'}
                title={'Verified'}
              >
                <RadioGroup.Input
                  id={`${props.name}1`}
                  label="True"
                  value="true"
                  disabled={checkDisabled(`locations[${index}].verified`) || search}
                />
                <RadioGroup.Input
                  id={`${props.name}2`}
                  label="False"
                  value="false"
                  disabled={checkDisabled(`locations[${index}].verified`) || search}
                />
              </RadioGroup>
            );
          }}
        />
      </Form.Row>

      {isAddPractitionerContacts && (
        <React.Fragment>
          <CheckboxRow>
            <Form.Controller
              name={`locations[${index}].addContact`}
              defaultValue={addContact}
              isCheckbox
              render={(props: any) => (
                <CheckboxInput
                  onChange={handleCheckboxChange}
                  checked={getValues(props.name) ? !!getValues(props.name) : false}
                  name={props.name}
                  id={props.name}
                  label="Add Practitioner Specific Contact Information for This Location"
                />
              )}
            />
          </CheckboxRow>

          {addContact && (
            <React.Fragment>
              <Form.RowLabel>Practitioner Specific Contact Information</Form.RowLabel>
              <Form.Row>
                <Form.Controller
                  as={PhoneNumberInput}
                  type="tel"
                  name={`locations[${index}].practitionerContact.phoneNumber`}
                  label="HP Direct Phone Number"
                  placeholder="HP Direct Phone Number"
                  rules={utils.validate('phoneNumber')}
                  defaultValue={field?.practitionerContact?.phoneNumber}
                  errorMessage={
                    errors?.locations?.[index]?.practitionerContact?.phoneNumber?.message
                  }
                  disabled={checkDisabled(`locations[${index}].practitionerContact.phoneNumber`)}
                />

                <Form.Controller
                  as={PhoneNumberInput}
                  type="tel"
                  name={`locations[${index}].practitionerContact.faxNumber`}
                  label="HP Direct Fax Number"
                  placeholder="HP Direct Fax Number"
                  rules={utils.validate('faxNumber')}
                  defaultValue={field?.practitionerContact?.faxNumber}
                  errorMessage={errors?.locations?.[index]?.practitionerContact?.faxNumber?.message}
                  disabled={checkDisabled(`locations[${index}].practitionerContact.faxNumber`)}
                />

                <Form.Controller
                  as={TextInput}
                  type="text"
                  name={`locations[${index}].practitionerContact.emailAddress`}
                  label="HP Direct Email Address"
                  placeholder="HP Direct Email Address"
                  rules={utils.validate('emailAddress')}
                  defaultValue={field?.practitionerContact?.emailAddress}
                  errorMessage={
                    errors?.locations?.[index]?.practitionerContact?.emailAddress?.message
                  }
                  disabled={checkDisabled(`locations[${index}].practitionerContact.emailAddress`)}
                />
              </Form.Row>

              <Form.RowLabel>Practitioner Specific Location Status</Form.RowLabel>
              <Form.Row>
                <Form.Controller
                  as={''}
                  name={`locations[${index}].practitionerContact.deactivated`}
                  label="Deactivated"
                  defaultValue={field?.practitionerContact?.deactivated || 'false'}
                  render={(props: any) => {
                    return (
                      <RadioGroup
                        name={props.name}
                        onChange={props.onChange}
                        initialValue={field?.practitionerContact?.deactivated || 'false'}
                        title={'Deactivated'}
                      >
                        <RadioGroup.Input
                          id={`${props.name}1`}
                          label="True"
                          value="true"
                          disabled={checkDisabled(
                            `locations[${index}].practitionerContact.deactivated`
                          )}
                        />
                        <RadioGroup.Input
                          id={`${props.name}2`}
                          label="False"
                          value="false"
                          disabled={checkDisabled(
                            `locations[${index}].practitionerContact.deactivated`
                          )}
                        />
                      </RadioGroup>
                    );
                  }}
                />

                <Form.Controller
                  as={''}
                  name={`locations[${index}].practitionerContact.verified`}
                  label="Verified"
                  defaultValue={field?.practitionerContact?.verified || 'false'}
                  render={(props: any) => {
                    return (
                      <RadioGroup
                        name={props.name}
                        onChange={props.onChange}
                        initialValue={field?.practitionerContact?.verified || 'false'}
                        title={'Verified'}
                      >
                        <RadioGroup.Input
                          id={`${props.name}1`}
                          label="True"
                          value="true"
                          disabled={checkDisabled(
                            `locations[${index}].practitionerContact.verified`
                          )}
                        />
                        <RadioGroup.Input
                          id={`${props.name}2`}
                          label="False"
                          value="false"
                          disabled={checkDisabled(
                            `locations[${index}].practitionerContact.verified`
                          )}
                        />
                      </RadioGroup>
                    );
                  }}
                />
              </Form.Row>
            </React.Fragment>
          )}
        </React.Fragment>
      )}

      {isShowingModal && (
        <Modal
          width="500px"
          title="Confirmation"
          showButtons
          onPress={handleRemovePractitionerContacts}
          onClose={() => setShowingModal(false)}
        >
          Are you sure you want to remove Practitioner Specific Contact Information for This
          Location?
        </Modal>
      )}
    </React.Fragment>
  );
};

export default LocationInputs;
